import React, { createContext, useContext, useMemo } from 'react';
import { ConfigProvider, theme as antTheme, App } from 'antd';

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
    // Define our custom theme tokens
    // Using the "Github Dark" palette style as requested/observed
    const customTheme = useMemo(() => ({
        colors: {
            // Base backgrounds
            bgBase: '#0d1117',      // Very dark background
            bgSurface: '#161b22',   // Surface/Card background (slightly lighter)
            bgElevated: '#21262d',  // Dropdowns/Modals

            // Borders
            border: '#30363d',

            // Text
            textPrimary: '#c9d1d9',
            textSecondary: '#8b949e',

            // Branding
            primary: '#1677ff',     // Ant Design Blue (or custom if needed)

            // Functional
            success: '#238636',
            error: '#da3633',
            warning: '#9e6a03',
        }
    }), []);

    // Helper algorithm to map our custom tokens to Ant Design's requirements
    const getAntTheme = (customColors) => ({
        algorithm: antTheme.darkAlgorithm,
        token: {
            colorBgBase: customColors.bgBase,
            colorBgContainer: customColors.bgSurface,
            colorBgElevated: customColors.bgElevated,
            colorBorder: customColors.border,
            colorText: customColors.textPrimary,
            colorTextSecondary: customColors.textSecondary,
            colorPrimary: customColors.primary,
            borderRadius: 6,
        },
        components: {
            Layout: {
                headerBg: customColors.bgSurface,
                bodyBg: customColors.bgBase,
                siderBg: customColors.bgSurface,
            },
            Card: {
                colorBgContainer: customColors.bgSurface,
                colorBorderSecondary: customColors.border,
            },
            Modal: {
                contentBg: customColors.bgSurface,
                headerBg: customColors.bgSurface,
            },
            Drawer: {
                colorBgElevated: customColors.bgSurface,
            },
            Message: {
                contentBg: customColors.bgElevated,
                colorText: customColors.textPrimary,
            },
            Notification: {
                colorBgElevated: customColors.bgElevated,
                colorText: customColors.textPrimary,
            }
        }
    });

    return (
        <ThemeContext.Provider value={customTheme}>
            <ConfigProvider theme={getAntTheme(customTheme.colors)}>
                <App>
                    {children}
                </App>
            </ConfigProvider>
        </ThemeContext.Provider>
    );
};
